<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import PlanCard from './PlanCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['show-payment-form']);

const store = useStore();
const { currentAccount } = useAccount();
const { t } = useI18n();

const dialogRef = ref(null);
const selectedPlan = ref(null);
const isUpgrading = ref(false);

const planOrder = plan => {
  const match = String(plan?.name || '').match(/^(\d+)/);
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
};

const plans = computed(() => {
  const allPlans = store.state.accounts.plans || [];

  return [...allPlans].sort((a, b) => {
    const orderDifference = planOrder(a) - planOrder(b);
    if (orderDifference !== 0) {
      return orderDifference;
    }

    const priceA = a.amount_cents || 0;
    const priceB = b.amount_cents || 0;
    if (priceA !== priceB) {
      return priceA - priceB;
    }

    return String(a.name || '').localeCompare(String(b.name || ''));
  });
});
const isFetchingPlans = computed(
  () => store.state.accounts.uiFlags.isFetchingPlans
);

const currentPlanName = computed(
  () => currentAccount.value?.custom_attributes?.plan_name
);

const normalizePlanValue = value => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const planIdentifiers = value => {
  const normalized = normalizePlanValue(value);
  if (!normalized) {
    return [];
  }

  const withoutNumericPrefix = normalized.replace(/^\d+/, '');
  return [...new Set([normalized, withoutNumericPrefix].filter(Boolean))];
};

const currentPlanCode = computed(
  () => currentAccount.value?.custom_attributes?.plan_code
);

const isCurrentPlan = plan => {
  const currentIdentifiers = [
    ...planIdentifiers(currentPlanName.value),
    ...planIdentifiers(currentPlanCode.value),
  ];

  const planValues = [...planIdentifiers(plan.name), ...planIdentifiers(plan.code)];

  if (!currentIdentifiers.length || !planValues.length) {
    return false;
  }

  return currentIdentifiers.some(identifier => planValues.includes(identifier));
};

const currentPlanAmountCents = computed(() => {
  const currentPlan = plans.value.find(plan => isCurrentPlan(plan));
  if (!currentPlan) {
    return null;
  }

  return Number(currentPlan.amount_cents || 0);
});

const isPlanUpgradeable = plan => {
  if (isCurrentPlan(plan) || currentPlanAmountCents.value === null) {
    return !isCurrentPlan(plan);
  }

  return Number(plan.amount_cents || 0) > currentPlanAmountCents.value;
};

const isPlanDowngrade = plan => {
  if (isCurrentPlan(plan) || currentPlanAmountCents.value === null) {
    return false;
  }

  return Number(plan.amount_cents || 0) < currentPlanAmountCents.value;
};

const fetchPlans = () => {
  store.dispatch('accounts/fetchPlans');
};

const hasPaymentMethod = computed(
  () => currentAccount.value?.custom_attributes?.payment_method_added === true
);

const handlePlanSelect = plan => {
  if (!isPlanUpgradeable(plan)) {
    useAlert(t('BILLING_SETTINGS.PLANS.ONLY_UPGRADES_ALLOWED'));
    return;
  }

  // Check if payment method is added
  if (!hasPaymentMethod.value) {
    // Emit event to parent to show payment method form
    emit('show-payment-form');
    return;
  }

  selectedPlan.value = plan;
  dialogRef.value?.open();
};

const confirmUpgrade = async () => {
  if (!selectedPlan.value) return;

  isUpgrading.value = true;
  try {
    await store.dispatch('accounts/upgradePlan', selectedPlan.value.code);
    useAlert(
      t('BILLING_SETTINGS.PLANS.UPGRADE_SUCCESS', {
        plan: selectedPlan.value.name,
      })
    );
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING_SETTINGS.PLANS.UPGRADE_ERROR')
    );
  } finally {
    isUpgrading.value = false;
  }
};

const closeDialog = () => {
  selectedPlan.value = null;
};

onMounted(() => {
  fetchPlans();
});

defineExpose({ fetchPlans });
</script>

<template>
  <div>
    <!-- Loading State -->
    <div v-if="isFetchingPlans" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"
        />
        <p class="mt-4 text-sm text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PLANS.LOADING') }}
        </p>
      </div>
    </div>

    <!-- Plans Grid -->
    <div
      v-else-if="plans.length > 0"
      class="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
    >
      <PlanCard
        v-for="plan in plans"
        :key="plan.lago_id"
        :plan="plan"
        :is-current-plan="isCurrentPlan(plan)"
        :is-selectable="isPlanUpgradeable(plan)"
        :is-downgrade="isPlanDowngrade(plan)"
        @select="handlePlanSelect"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <svg
        class="w-16 h-16 text-n-slate-6 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <p class="text-lg font-medium text-n-slate-12 mb-2">
        {{ $t('BILLING_SETTINGS.PLANS.NO_PLANS') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('BILLING_SETTINGS.PLANS.NO_PLANS_DESC') }}
      </p>
    </div>

    <!-- Confirmation Dialog -->
    <Dialog
      ref="dialogRef"
      :title="$t('BILLING_SETTINGS.PLANS.CONFIRM_UPGRADE_TITLE')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="closeDialog"
    >
      <div v-if="selectedPlan" class="space-y-4">
        <p class="text-sm text-n-slate-11">
          {{
            $t('BILLING_SETTINGS.PLANS.CONFIRM_UPGRADE_DESC', {
              plan: selectedPlan.name,
              price: selectedPlan.formatted_price,
            })
          }}
        </p>

        <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
          <p class="text-sm font-medium text-n-slate-12 mb-2">
            {{ selectedPlan.name }}
          </p>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ selectedPlan.formatted_price }}
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="$t('BILLING_SETTINGS.PLANS.CANCEL')"
            class="w-full"
            :disabled="isUpgrading"
            @click="dialogRef?.close()"
          />
          <Button
            color="blue"
            :label="$t('BILLING_SETTINGS.PLANS.CONFIRM')"
            class="w-full"
            :is-loading="isUpgrading"
            @click="confirmUpgrade"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
